import Snap from './snap'
import SnapArrow from './guides/arrow'
import _ from 'lodash'

const SpacingSnapArrow = SnapArrow.extend({
  /*
   * Determines if it should increase the arrow's thickness. We do this if it's
   * overlapping with another guide being displayed on the canvas.
   */
  adjust: function (coord, segmentStart, segmentDimension, guides) {
    // Find which other guides are at this coord
    let overlapped = guides.filter(g => g != this && g.coords && g.coords[coord] == this.coords[coord])
                           .find(guide => {
                              // See if we're inside of this guide
                              let overlapping = (this.coords[segmentStart] >= guide.coords[segmentStart]) && (this.coords[segmentStart] + this.coords[segmentDimension] <= guide.coords[segmentStart] + guide.coords[segmentDimension])
                              return overlapping
                           })

    if (overlapped) {
      this.$el.addClass('overlapping')
    }

    return this
  }
})

/*
 * A snap type that snaps based on other wob positions. Only works if there are at least 3 wobs.
 * Tries to create equal padding between 3 more wobs.
 */
const ElementSpacingSnap = Snap.extend({

  getGuides: function (area, previousArea, handleType) {
    let guides = []

    if (this.canvas.collection.length < 3) {
      // Can't compare equal relative spacing if we don't have at least 3 wobs
      return guides
    }

    let areas = this.areas(area)
      , current = areas.shift()
      , contenders = areas.filter(this.filterByPlane.bind(this, current)) // Find all the wobs that are on the same plane
                          .sort(this.sort.bind(this))

    /*
     * Builds an object storing all the relative spacing values, where each item
     * is key'd based on the relative spacing value, and the value is an array of all wobs
     * that have the key value relative spacing to another wob
     */
    let relatives = contenders.reduce((spacing, c, i) => {
      // Find the relative spacing between the current wob, `c` and all of the other wobs
      for (let j = 0; j < contenders.length; j++) {
        // If the wobs intersect on this plane and the current wob end point is before the end point of the next object
        if (this.filterByPlane(c, contenders[j]) && c[this.end] < contenders[j][this.direction]) {
          var space = contenders[j][this.direction] - c[this.end]
          ;(spacing[space] || (spacing[space] = [])).push({from: c, to: contenders[j]})
        }
      }
      return spacing
    }, {})

    // See if the `current` wob is close to any values in the relatives object
    Object.keys(relatives).forEach(spacing => {
      relatives[spacing].forEach(relative => {
        // First check if the current moving wob centered between any of the wobs in this current relative spacing value
        let beforeSpacing = current[this.direction] - relative.from[this.end]
          , afterSpacing = relative.to[this.direction] - current[this.end]

        if (beforeSpacing > 0 && afterSpacing > 0 && this.withinThreshold(beforeSpacing - afterSpacing, this.threshold * 2)) {
          let diff = afterSpacing - beforeSpacing
          if (!handleType) {
            // they are dragging between two wobs, we ony want to snap to half the space
            diff /= 2
          }

          guides.push(new this.snapLine({direction: this.direction, from: previousArea, to: relative.from, canvas: this.canvas, active: 'from', diff })) // Left snap arrow
          guides.push(new this.snapLine({direction: this.direction, from: relative.to, to: previousArea, canvas: this.canvas, active: 'to', diff })) // Right snap arrow
        }

        // Check if we have equal spacing for wobs before the moving wob
        if (this.withinThreshold(relative.to[this.end] + parseInt(spacing, 10) - current[this.direction])) {
          // The line between the moving wob and whatever we triggered
          let diff = relative.to[this.end] + parseInt(spacing, 10) - current[this.direction]

          guides.push(new this.snapLine({direction: this.direction, from: previousArea, to: relative.to, canvas: this.canvas, active: 'from', diff}))
          relatives[spacing].forEach(relative => guides.push(new this.snapLine({direction: this.direction, from: relative.to, to: relative.from, canvas: this.canvas, diff})))
        }

        // Check wobs relative spacing with wobs after this wob
        if (this.withinThreshold(relative.from[this.direction] - parseInt(spacing, 10) - current[this.end])) {
          // The line between the moving wob and whatever we triggered
          let diff = relative.from[this.direction] - parseInt(spacing, 10) - current[this.end]

          guides.push(new this.snapLine({direction: this.direction, from: relative.from, to: previousArea, canvas: this.canvas, active: 'to', diff }))
          relatives[spacing].forEach(relative => guides.push(new this.snapLine({direction: this.direction, from: relative.to, to: relative.from, canvas: this.canvas, diff})))
        }
      })
    })

    return _.uniqWith(guides, (obj1, obj2) => {
      return obj2.to == obj1.to && obj2.from == obj1.from
    })
  },

  sort: function (a, b) {
    // Sorts so the objects go from left to right or top to bottom
    return a[this.direction] - b[this.direction]
  },

})

const HorizontalSnapArrow = SpacingSnapArrow.extend({

  type: 'horizontal',

  adjust: _.partial(SpacingSnapArrow.prototype.adjust, 'top', 'left', 'width'),

  coords: function (wobA, wobB) {
    // Determine the smaller of the two wobs (using height) and use it for the center point
    var lockedOn = wobA.height < wobB.height ? wobA : wobB
      , other = wobA.height < wobB.height ? wobB : wobA
      , offset = lockedOn.top + lockedOn.height / 2 + this.canvas.padding

    if (other.top > lockedOn.top) {
      // other top is below locked on, so adjust the top so it's lower
      offset -= (lockedOn.top - other.top) / 2
      offset = Math.floor(offset)
    } else if (other.bottom < lockedOn.bottom) {
      // the bottom of the other is above the locked on, the line should move up
      offset += (other.bottom - lockedOn.bottom) / 2
      offset = Math.ceil(offset)
    }

    return {
      width: wobA.left - wobB.right - this.offset * 2,
      left: wobB.right + this.canvas.padding + this.offset,
      top: offset
    }
  }
})

const VerticalSnapArrow = SpacingSnapArrow.extend({

  type: 'vertical',

  adjust: _.partial(SpacingSnapArrow.prototype.adjust, 'left', 'top', 'height'),

  coords: function (wobA, wobB) {
    // Determine the smaller of the two wobs (using width) and use it for the center point
    var lockedOn = wobA.width < wobB.width ? wobA : wobB
      , other = wobA.width < wobB.width ? wobB : wobA
      , offset = lockedOn.left + lockedOn.width / 2 + this.canvas.padding

    if (other.left > lockedOn.left) {
      // other left is below locked on, so adjust the left
      offset -= (lockedOn.left - other.left) / 2
    } else if (other.right < lockedOn.right) {
      // the right of the other is right the locked on, the line should to the left
      offset += (other.right - lockedOn.right) / 2
    }

    return {
      height: wobA.top - wobB.bottom - this.offset * 2,
      top: wobB.bottom + this.canvas.padding + this.offset,
      left: offset
    }
  }
})


const HorizontalElementSpacingSnap = ElementSpacingSnap.extend({

  // Css property to set the start of this shape
  direction: 'left',

  // Property to grab the end coordinates of the shape
  end: 'right',

  lengthProp: 'width',

  snapLine: HorizontalSnapArrow,

  filterByPlane: ElementSpacingSnap.prototype.horizontalIntersect,

})

const VerticalElementSpacingSnap = ElementSpacingSnap.extend({

  // Css property to set the start of this shape
  direction: 'top',

  // Property to grab the end coordinates of the shape
  end: 'bottom',

  lengthProp: 'height',

  snapLine: VerticalSnapArrow,

  filterByPlane: ElementSpacingSnap.prototype.verticalIntersect,

})

export const Vertical = VerticalElementSpacingSnap
export const Horizontal = HorizontalElementSpacingSnap
